import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  UIButton,
  UIColor,
  UIFont,
  UIImage,
  UITag,
  UIText,
  UIToggle,
} from "../types";
import { EMPTY_UI_BUTTON, EMPTY_UI_TOGGLE } from "../utils/types";
import { v4 as uuid } from "uuid";
import pick from "lodash/pick";
import {
  trackProductSelect,
  trackProductUnselect,
  trackShowOnlyDifferencesToggle,
} from "../analytics";

export enum FeatureType {
  BLANK,
  CHECKED,
  ICON,
  SUB_ITEM,
}

export enum ChartStatus {
  INITIAL,
  LOADING,
  LOADED,
}

const initialState: ChartState = {
  status: ChartStatus.INITIAL,
  data: {
    styling: {
      filterButton: {},
      tag: {},
      productApplication: {},
      legalCopy: {},
      column: {},
      shopButton: {},
      shopSelectButton: {},
    },
    useCustomProductFilters: false,
    showAdditionalFilterButton: false,
    productKindFilters: [],
    showPrice: false,
    showShopButton: false,
    products: [],
    modal: {
      openButton: EMPTY_UI_BUTTON,
      closeButton: EMPTY_UI_BUTTON,
      showAdditionalFilterButton: false,
      additionalFilterButton: EMPTY_UI_BUTTON,
    },
    showOnlyDiffsToggle: EMPTY_UI_TOGGLE,
    showOnlyDiffs: false,
    showProductApplication: false,
    showBestFor: false,
    showShopSelectSection: false,
    shopSelectOpenOnLoad: false,
    featureArray: [],
    featureMap: {},
    productFilters: [],
    mobile: {
      chooseProducts: [],
      chosenProducts: [null, null],
      selectedProducts: [],
      selectedFeatureMap: {},
      legalCopyArray: [],
    },
    desktop: {
      customProductFilters: [],
      filteredProducts: [],
      selectedProductKindFilters: [],
      selectedProducts: [],
      selectedFeatureMap: {},
      legalCopyArray: [],
    },
    sections: [],
    showStats: false,
    showFooter: false,
  },
};

// this filter is used when useCustomProductFilters is false in mobile
const DEFAULT_FILTER = {
  id: "NO_ID",
  categoryTitle: "NO_CATEGORY",
  filterButtonCopy: "NO_COPY",
  productKindId: "NO_KIND",
  preselected: true,
};

export const chartSlice = createSlice({
  name: "chart",
  initialState,
  reducers: {
    setChartData: (state, action: PayloadAction<ChartDataState>) => {
      state.status = ChartStatus.LOADED;

      const {
        styling,
        backgroundColor,
        header,
        instructions,
        useCustomProductFilters,
        productKindFilters,
        showAdditionalFilterButton,
        additionalFilterButton,
        showPrice,
        showShopButton,
        showShopSelectSection,
        shopSelectTitle,
        shopSelectMobileTitleTextColor,
        shopSelectMobileTitleRowColor,
        shopSelectDescription,
        shopSelectOpenOnLoad,
        modal,
        showOnlyDiffsToggle,
        showProductApplication,
        productApplicationTitle,
        showBestFor,
        bestForTitle,
        bestForIconColor,
        bestForHoverIconColor,
        products,
        sections,
        legalCopy,
        showStats,
        stats,
        showFooter,
        footer,
      } = action.payload;

      state.data.styling = styling;
      state.data.backgroundColor = backgroundColor;
      state.data.header = header;
      state.data.instructions = instructions;
      state.data.useCustomProductFilters = useCustomProductFilters;
      state.data.showAdditionalFilterButton = showAdditionalFilterButton;
      state.data.additionalFilterButton = additionalFilterButton;
      state.data.showPrice = showPrice;
      state.data.showShopButton = showShopButton;
      state.data.showShopSelectSection = showShopSelectSection;
      state.data.shopSelectTitle = shopSelectTitle;
      state.data.shopSelectMobileTitleTextColor =
        shopSelectMobileTitleTextColor;
      state.data.shopSelectMobileTitleRowColor = shopSelectMobileTitleRowColor;
      state.data.shopSelectDescription = shopSelectDescription;
      state.data.shopSelectOpenOnLoad = shopSelectOpenOnLoad;
      state.data.modal = modal;
      state.data.showOnlyDiffsToggle = showOnlyDiffsToggle;
      state.data.showProductApplication = showProductApplication;
      state.data.productApplicationTitle = productApplicationTitle;
      state.data.showBestFor = showBestFor;
      state.data.bestForTitle = bestForTitle;
      state.data.bestForIconColor = bestForIconColor;
      state.data.bestForHoverIconColor = bestForHoverIconColor;
      state.data.products = products;
      state.data.sections = sections;
      state.data.legalCopy = legalCopy;
      state.data.showStats = showStats;
      state.data.stats = stats;
      state.data.showFooter = showFooter;
      state.data.footer = footer;

      state.data.productKindFilters = products.reduce((acc, product) => {
        const productKindFilter = productKindFilters.find(
          (filter) => filter.productKindId === product.productKindId,
        );
        if (
          productKindFilter &&
          !acc.find(
            (filter) =>
              filter.productKindId === productKindFilter.productKindId,
          )
        ) {
          acc.push(productKindFilter);
        }
        return acc;
      }, [] as ProductKindFilterState[]);

      // - Feature Map
      state.data.featureArray = sections.flatMap((section) =>
        section.subSections.flatMap((subSection) => subSection.features),
      );
      state.data.featureMap = state.data.featureArray.reduce(
        (accFeature, feature) => {
          const productFeatureMap = products.reduce((accProduct, product) => {
            if (product.isBundle) {
              const leftProductFeature = product.leftData.features.find(
                (productFeature) => productFeature.featureId === feature.id,
              );
              const rightProductFeature = product.rightData.features.find(
                (productFeature) => productFeature.featureId === feature.id,
              );
              accProduct[product.leftData.id] = getProductFeature(
                feature,
                leftProductFeature,
              );
              accProduct[product.rightData.id] = getProductFeature(
                feature,
                rightProductFeature,
              );
            } else {
              const productFeature = product.data.features.find(
                (productFeature) => productFeature.featureId === feature.id,
              );
              accProduct[product.id] = getProductFeature(
                feature,
                productFeature,
              );
            }
            return accProduct;
          }, {} as ProductFeatureMapState);
          accFeature[feature.id] = productFeatureMap;
          return accFeature;
        },
        {} as FeatureMapState,
      );

      // - Product Filters
      state.data.productFilters = products.map((product) => ({
        id: product.id,
        name: product.name,
      }));

      // - Mobile
      state.data.mobile.selectedProducts = products.map((product) => ({
        id: product.id,
        selected: product.mobilePreselected,
      }));
      if (useCustomProductFilters) {
        state.data.mobile.chooseProducts = state.data.productKindFilters.map(
          (productKindFilter) => ({
            filter: productKindFilter,
            products: products.filter(
              (product) =>
                product.productKindId === productKindFilter.productKindId,
            ),
          }),
        );
      } else {
        state.data.mobile.chooseProducts = [
          {
            filter: DEFAULT_FILTER,
            products,
          },
        ];
      }
      const mobileSelectedOnly = state.data.mobile.selectedProducts.filter(
        (selectedProduct) => selectedProduct.selected,
      );
      state.data.mobile.chosenProducts[0] = mobileSelectedOnly[0]
        ? mobileSelectedOnly[0].id
        : null;
      state.data.mobile.chosenProducts[1] = mobileSelectedOnly[1]
        ? mobileSelectedOnly[1].id
        : null;
      state.data.mobile.selectedFeatureMap = getSelectedFeatureMapForMobile(
        state.data,
      );
      state.data.mobile.legalCopyArray = getMobileLegacyCopyArray(state.data);

      // - Desktop
      state.data.desktop.customProductFilters = state.data.productKindFilters
        .filter(
          (productKindFilter) =>
            products.filter(
              (product) =>
                product.productKindId === productKindFilter.productKindId,
            ).length > 0,
        )
        .map((productKindFilter) => ({
          id: productKindFilter.id,
          name: productKindFilter.filterButtonCopy,
        }));

      state.data.desktop.selectedProductKindFilters =
        state.data.productKindFilters.map((productKindFilter) => ({
          id: productKindFilter.id,
          productKindId: productKindFilter.productKindId,
          selected: !!products.find(
            (product) =>
              product.productKindId === productKindFilter.productKindId &&
              product.desktopPreselected,
          ),
        }));
      state.data.desktop.filteredProducts = getFilteredProducts(state.data);
      state.data.desktop.selectedProducts = products.map((product) => ({
        id: product.id,
        selected: product.desktopPreselected,
      }));
      state.data.desktop.selectedFeatureMap = getSelectedFeatureMapForDesktop(
        state.data,
      );
      state.data.desktop.legalCopyArray = getDesktopLegacyCopyArray(state.data);
    },
    setChartStatus: (state, action: PayloadAction<ChartStatus>) => {
      state.status = action.payload;
    },
    toggleProductKind: (state, action: PayloadAction<string>) => {
      const selectedOnly = state.data.desktop.selectedProductKindFilters.filter(
        (selectedProductKindFilter) => selectedProductKindFilter.selected,
      );
      const selectedProductKind =
        state.data.desktop.selectedProductKindFilters.find(
          (selectedProductKindFilter) =>
            selectedProductKindFilter.id === action.payload,
        );
      if (
        selectedProductKind &&
        (!selectedProductKind.selected ||
          (selectedProductKind.selected && selectedOnly.length > 1))
      ) {
        selectedProductKind.selected = !selectedProductKind.selected;
      }
      state.data.desktop.filteredProducts = getFilteredProducts(state.data);
      state.data.desktop.selectedProducts.forEach((selectedProduct) => {
        if (selectedProduct.selected) {
          const shouldBeSelected =
            state.data.desktop.filteredProducts.filter(
              (filteredProduct) =>
                filteredProduct.products.filter(
                  (product) => product.id === selectedProduct.id,
                ).length > 0,
            ).length > 0;
          if (!shouldBeSelected) {
            selectedProduct.selected = false;
          }
        }
      });
    },
    toggleProduct: (
      state,
      action: PayloadAction<{ productId: string; isMobile: boolean }>,
    ) => {
      const { isMobile, productId } = action.payload;
      if (isMobile) {
        const selectedProduct = state.data.mobile.selectedProducts.find(
          (selectedProduct) => selectedProduct.id === productId,
        );
        if (selectedProduct) {
          const selectedOnly = state.data.mobile.selectedProducts.filter(
            (selected) =>
              selected.selected && selected.id !== selectedProduct.id,
          );
          if (
            selectedProduct.selected ||
            (!selectedProduct.selected && selectedOnly.length < 2)
          ) {
            selectedProduct.selected = !selectedProduct.selected;
          }

          const mobileSelectedOnly = state.data.mobile.selectedProducts.filter(
            (selectedProduct) => selectedProduct.selected,
          );
          state.data.mobile.chosenProducts[0] = mobileSelectedOnly[0]
            ? mobileSelectedOnly[0].id
            : null;
          state.data.mobile.chosenProducts[1] = mobileSelectedOnly[1]
            ? mobileSelectedOnly[1].id
            : null;
          state.data.mobile.selectedFeatureMap = getSelectedFeatureMapForMobile(
            state.data,
          );
          state.data.mobile.legalCopyArray = getMobileLegacyCopyArray(
            state.data,
          );

          const selectedProductState = state.data.products.find(
            (product) => product.id === selectedProduct.id,
          );
          if (selectedProductState) {
            selectedProduct.selected &&
              trackProductSelect(selectedProductState);
            !selectedProduct.selected &&
              trackProductUnselect(selectedProductState);
          }
        }
      } else {
        const selectedProduct = state.data.desktop.selectedProducts.find(
          (selectedProduct) => selectedProduct.id === productId,
        );
        if (selectedProduct) {
          selectedProduct.selected = !selectedProduct.selected;

          state.data.desktop.selectedFeatureMap =
            getSelectedFeatureMapForDesktop(state.data);
          state.data.desktop.legalCopyArray = getDesktopLegacyCopyArray(
            state.data,
          );

          const selectedProductState = state.data.products.find(
            (product) => product.id === selectedProduct.id,
          );
          if (selectedProductState) {
            selectedProduct.selected &&
              trackProductSelect(selectedProductState);
            !selectedProduct.selected &&
              trackProductUnselect(selectedProductState);
          }
        }
      }
    },
    selectProductByColumn: (
      state,
      action: PayloadAction<{
        productId: string;
        column: number;
      }>,
    ) => {
      const { productId, column } = action.payload;
      const productColumn = state.data.mobile.chosenProducts[column];
      if (productId === productColumn) {
        state.data.mobile.chosenProducts[column] = null;
      } else {
        state.data.mobile.chosenProducts[column] = productId;
      }
      state.data.mobile.selectedProducts =
        state.data.mobile.selectedProducts.map((selectedProduct) => ({
          id: selectedProduct.id,
          selected: state.data.mobile.chosenProducts.includes(
            selectedProduct.id,
          ),
        }));
      state.data.mobile.selectedFeatureMap = getSelectedFeatureMapForMobile(
        state.data,
      );
      state.data.mobile.legalCopyArray = getMobileLegacyCopyArray(state.data);
    },
    toggleShowOnlyDiffs: (
      state,
      action: PayloadAction<{ isMobile: boolean }>,
    ) => {
      state.data.showOnlyDiffs = !state.data.showOnlyDiffs;
      if (action.payload.isMobile) {
        state.data.mobile.legalCopyArray = getMobileLegacyCopyArray(state.data);
      } else {
        state.data.desktop.legalCopyArray = getDesktopLegacyCopyArray(
          state.data,
        );
      }
      trackShowOnlyDifferencesToggle(state.data.showOnlyDiffs);
    },
  },
});

export const {
  setChartData,
  setChartStatus,
  toggleProductKind,
  toggleProduct,
  selectProductByColumn,
  toggleShowOnlyDiffs,
} = chartSlice.actions;

export default chartSlice.reducer;

function getFilteredProducts(
  state: ChartDataLoadedState,
): FilteredProductState[] {
  const {
    useCustomProductFilters,
    products,
    productKindFilters,
    desktop: { selectedProductKindFilters },
  } = state;
  if (useCustomProductFilters) {
    return productKindFilters
      .filter(
        (productKindFilter) =>
          selectedProductKindFilters.findIndex(
            (selectedProductKindFilter) =>
              selectedProductKindFilter.id === productKindFilter.id &&
              selectedProductKindFilter.selected,
          ) >= 0,
      )
      .map((productKindFilter) => ({
        filter: productKindFilter,
        products: products.filter(
          (product) =>
            product.productKindId === productKindFilter.productKindId,
        ),
      }));
  }
  return [];
}

function getSelectedFeatureMapForMobile(state: ChartDataLoadedState) {
  return state.featureArray.reduce((accFeature, feature) => {
    const productIds: string[] = [];
    if (state.mobile.chosenProducts[0]) {
      const product = state.products.find(
        (product) => product.id === state.mobile.chosenProducts[0],
      );
      if (product && product.isBundle) {
        productIds.push(product.leftData.id);
        productIds.push(product.rightData.id);
      } else {
        productIds.push(state.mobile.chosenProducts[0]);
      }
    }
    if (state.mobile.chosenProducts[1]) {
      const product = state.products.find(
        (product) => product.id === state.mobile.chosenProducts[1],
      );
      if (product && product.isBundle) {
        productIds.push(product.leftData.id);
        productIds.push(product.rightData.id);
      } else {
        productIds.push(state.mobile.chosenProducts[1]);
      }
    }
    const productFeatureMap = pick(state.featureMap[feature.id], productIds);
    const hasDifferences = checkDifferences(productIds, productFeatureMap);
    accFeature[feature.id] = {
      productFeatureMap,
      hasDifferences,
    };
    return accFeature;
  }, {} as SelectedFeatureMapState);
}

function getSelectedFeatureMapForDesktop(state: ChartDataLoadedState) {
  return state.featureArray.reduce((accFeature, feature) => {
    const productIds: string[] = state.desktop.selectedProducts
      .filter((selectedProduct) => selectedProduct.selected)
      .flatMap((selectedProduct) => {
        const product = state.products.find(
          (product) => product.id === selectedProduct.id,
        );
        if (product && product.isBundle) {
          return [product.leftData.id, product.rightData.id];
        }
        return selectedProduct.id;
      });
    const productFeatureMap = pick(state.featureMap[feature.id], productIds);
    const hasDifferences = checkDifferences(productIds, productFeatureMap);
    accFeature[feature.id] = {
      productFeatureMap,
      hasDifferences,
    };
    return accFeature;
  }, {} as SelectedFeatureMapState);
}

function checkDifferences(
  productIds: string[],
  productFeatureMap: ProductFeatureMapState,
) {
  if (!productIds.length) {
    return false;
  }
  return !productIds.every((productId) => {
    const ref = productFeatureMap[productIds[0]];
    const current = productFeatureMap[productId];

    if (current.type !== ref.type) {
      return false;
    }

    if (
      current.type === FeatureType.BLANK ||
      current.type === FeatureType.CHECKED
    ) {
      return true;
    }

    // this is here just to avoid using TS casting to get the featureItemId
    if (ref.type === FeatureType.BLANK || ref.type === FeatureType.CHECKED) {
      return true;
    }

    return current.featureItemId === ref.featureItemId;
  });
}

function getProductFeature(
  feature: FeatureState,
  productFeature: ProductFeatureDataState | undefined,
): ProductFeatureState {
  if (productFeature && productFeature.featureItemId) {
    if (productFeature.icon) {
      return {
        id: productFeature.id,
        featureId: feature.id,
        type: FeatureType.ICON,
        featureItemId: productFeature.featureItemId,
        icon: productFeature.icon,
        itemLegalCopy: productFeature.itemLegalCopy,
      };
    }
    if (productFeature.item || productFeature.subItem) {
      return {
        id: productFeature.id,
        featureId: feature.id,
        type: FeatureType.SUB_ITEM,
        featureItemId: productFeature.featureItemId,
        item: productFeature.item,
        subItem: productFeature.subItem,
        itemLegalCopy: productFeature.itemLegalCopy,
      };
    }
  }
  if (productFeature && !productFeature.featureItemId) {
    return {
      id: productFeature.id,
      featureId: feature.id,
      type: FeatureType.CHECKED,
    };
  }
  return {
    id: uuid(),
    featureId: feature.id,
    type: FeatureType.BLANK,
  };
}

function getMobileLegacyCopyArray(state: ChartDataLoadedState): string[] {
  const [productId1, productId2] = state.mobile.chosenProducts;
  const product1 =
    productId1 && state.products.find((product) => product.id === productId1);
  const product2 =
    productId2 && state.products.find((product) => product.id === productId2);
  const legaCopySet = state.featureArray.reduce((acc, feature) => {
    const productFeatureMap = state.mobile.selectedFeatureMap[feature.id];
    if (!state.showOnlyDiffs || productFeatureMap?.hasDifferences) {
      feature.legalCopy && acc.add(feature.legalCopy);
      if (product1) {
        const legalCopy1 = getLegalCopyByProduct(productFeatureMap, product1);
        legalCopy1.forEach((copy) => acc.add(copy));
      }
      if (product2) {
        const legalCopy2 = getLegalCopyByProduct(productFeatureMap, product2);
        legalCopy2.forEach((copy) => acc.add(copy));
      }
    }
    return acc;
  }, new Set<string>());
  return [...legaCopySet];
}

function getDesktopLegacyCopyArray(state: ChartDataLoadedState): string[] {
  const selectedProducts = state.products.filter((product) =>
    state.desktop.selectedProducts.find(
      (selectedProduct) =>
        selectedProduct.id === product.id && selectedProduct.selected,
    ),
  );
  const legaCopySet = state.featureArray.reduce((acc, feature) => {
    const productFeatureMap = state.desktop.selectedFeatureMap[feature.id];
    if (!state.showOnlyDiffs || productFeatureMap?.hasDifferences) {
      feature.legalCopy && acc.add(feature.legalCopy);
      selectedProducts.forEach((product) => {
        const legalCopy = getLegalCopyByProduct(productFeatureMap, product);
        legalCopy.forEach((copy) => acc.add(copy));
      });
    }
    return acc;
  }, new Set<string>());
  return [...legaCopySet];
}

function getLegalCopyByProduct(
  productFeatureMap: SelectedProductFeatureMapState,
  product: ProductState,
): Set<string> {
  const legalCopySet = new Set<string>();
  if (product.isBundle) {
    const productFeature1 =
      productFeatureMap.productFeatureMap[product.leftData.id];
    const productFeature2 =
      productFeatureMap.productFeatureMap[product.rightData.id];
    const itemLegalCopy1 = getItemLegalCopy(productFeature1);
    const itemLegalCopy2 = getItemLegalCopy(productFeature2);
    itemLegalCopy1 && legalCopySet.add(itemLegalCopy1);
    itemLegalCopy2 && legalCopySet.add(itemLegalCopy2);
  } else {
    const productFeature = productFeatureMap.productFeatureMap[product.id];
    const itemLegalCopy = getItemLegalCopy(productFeature);
    itemLegalCopy && legalCopySet.add(itemLegalCopy);
  }
  return legalCopySet;
}

function getItemLegalCopy(
  productFeature: ProductFeatureState | undefined,
): string | undefined {
  if (
    productFeature &&
    (productFeature.type === FeatureType.ICON ||
      productFeature.type === FeatureType.SUB_ITEM)
  ) {
    return productFeature.itemLegalCopy;
  }
}

export interface ChartState {
  status: ChartStatus;
  data: ChartDataLoadedState;
}

export interface ChartDataLoadedState extends ChartDataState {
  // populated when loaded
  featureArray: FeatureState[];
  featureMap: FeatureMapState;
  productFilters: FilterState[];
  mobile: {
    chooseProducts: ChooseProductState[];
    chosenProducts: ChosenProducts;
    selectedProducts: SelectedProductState[];
    selectedFeatureMap: SelectedFeatureMapState;
    legalCopyArray: string[];
  };
  desktop: {
    customProductFilters: FilterState[];
    filteredProducts: FilteredProductState[];
    selectedProductKindFilters: SelectedProductKindFilterState[];
    selectedProducts: SelectedProductState[];
    selectedFeatureMap: SelectedFeatureMapState;
    legalCopyArray: string[];
  };
}

export interface ChartDataState {
  styling: GlobalStyleState;
  backgroundColor?: UIColor;
  header?: UIText;
  instructions?: UIText;
  legalCopy?: string;
  useCustomProductFilters: boolean;
  productKindFilters: ProductKindFilterState[];
  showAdditionalFilterButton: boolean;
  additionalFilterButton?: UIButton;
  modal: ModalState;
  showOnlyDiffsToggle: UIToggle;
  showOnlyDiffs: boolean;
  showProductApplication: boolean;
  productApplicationTitle?: UIText;
  showBestFor: boolean;
  bestForTitle?: UIText;
  bestForIconColor?: UIColor;
  bestForHoverIconColor?: UIColor;
  showPrice: boolean;
  showShopSelectSection: boolean;
  shopSelectTitle?: UIText;
  shopSelectMobileTitleTextColor?: UIColor;
  shopSelectMobileTitleRowColor?: UIColor;
  shopSelectDescription?: UIText;
  shopSelectOpenOnLoad: boolean;
  showShopButton: boolean;
  products: ProductState[];
  sections: SectionState[];
  showStats: boolean;
  stats?: StatsState[];
  showFooter: boolean;
  footer?: FooterState;
}

export interface GlobalStyleState {
  filterButton: {
    font?: UIFont;
    textColor?: UIColor;
    backgroundColor?: UIColor;
    borderColor?: UIColor;
    selected?: {
      textColor?: UIColor;
      backgroundColor?: UIColor;
      borderColor?: UIColor;
    };
  };
  tag: {
    font?: UIFont;
  };
  productApplication: {
    font?: UIFont;
    textColor?: UIColor;
  };
  legalCopy: {
    textColor?: UIColor;
  };
  column: {
    backgroundColor?: UIColor;
    bundleBackgroundColor?: UIColor;
  };
  shopButton: {
    copy?: string;
    textColor?: UIColor;
    borderColor?: UIColor;
    backgroundColor?: UIColor;
    hover?: {
      textColor?: UIColor;
      borderColor?: UIColor;
      backgroundColor?: UIColor;
    };
  };
  shopSelectButton: {
    textColor?: UIColor;
    borderColor?: UIColor;
    backgroundColor?: UIColor;
    hover?: {
      textColor?: UIColor;
      borderColor?: UIColor;
      backgroundColor?: UIColor;
    };
  };
}

export interface ProductKindFilterState {
  id: string;
  productKindId: string;
  filterButtonCopy: string;
  categoryTitle: string;
}

export interface SelectedProductKindFilterState {
  id: string;
  productKindId: string;
  selected: boolean;
}

export type ProductState = SingleProductState | BundleProductState;

export interface BaseProductState {
  id: string;
  isBundle: boolean;
  productKindId: string;
  name: string;
  sku?: string;
  exploreSku?: string;
  useTag: boolean;
  tag?: UITag;
  shopUrl?: string;
  price?: string;
  savePrice?: string;
  useSalesMessage: boolean;
  salesMessage?: UIText;
  application?: string;
  bestForIcons?: UIImage[];
  overview?: string;
  mobilePreselected: boolean;
  desktopPreselected: boolean;
  showShopSelectButton: boolean;
  shopSelectButton?: {
    url?: string;
    description?: string;
    buttonCopy?: string;
  };
}

export interface SingleProductState extends BaseProductState {
  isBundle: false;
  data: ProductDataState;
}

export interface BundleProductState extends BaseProductState {
  isBundle: true;
  leftData: BundleProductDataState;
  rightData: BundleProductDataState;
}

export interface ProductDataState {
  image: string;
  features: ProductFeatureDataState[];
}

export interface BundleProductDataState extends ProductDataState {
  id: string;
}

export interface SelectedProductState {
  id: string;
  selected: boolean;
}

export interface FilteredProductState {
  filter: ProductKindFilterState;
  products: ProductState[];
}

export type ChooseProductState = FilteredProductState;
export type ChosenProducts = [string | null, string | null];

export interface TooltipState {
  title?: string;
  description?: string;
  visible: boolean;
}

export interface FeatureState {
  id: string;
  icon?: UIImage;
  name: string;
  tooltip: TooltipState;
  legalCopy?: string;
}

export interface BaseSectionState {
  id: string;
  font?: UIFont;
  desktopTextColor?: UIColor;
  mobileTextColor?: UIColor;
  mobileBackgroundColor?: UIColor;
}

export interface SubSectionState extends BaseSectionState {
  name?: string;
  features: FeatureState[];
}

export interface SectionState extends BaseSectionState {
  name: string;
  collapsed: boolean;
  showProductOverview: boolean;
  productOverviewTitle?: string;
  subSections: SubSectionState[];
}

export interface FilterState {
  id: string;
  name: string;
}

export interface StatsState {
  title: UIText;
  description: UIText;
  img?: UIImage;
}

export interface FooterState {
  title: UIText;
  logos: UIImage[];
}

export interface FeatureMapState {
  [featureId: string]: ProductFeatureMapState;
}

export interface SelectedFeatureMapState {
  [featureId: string]: SelectedProductFeatureMapState;
}

export interface ProductFeatureMapState {
  [productId: string]: ProductFeatureState;
}

export type ProductFeatureState =
  | ProductFeatureBlank
  | ProductFeatureChecked
  | ProductFeatureIcon
  | ProductFeatureSubItem;

export interface SelectedProductFeatureMapState {
  productFeatureMap: ProductFeatureMapState;
  hasDifferences: boolean;
}

export interface ProductFeatureBase {
  id: string;
  featureId: string;
}

export interface ProductFeatureDataState extends ProductFeatureBase {
  featureItemId?: string;
  icon?: UIImage;
  item?: string;
  subItem?: string;
  itemLegalCopy?: string;
}

export interface ProductFeatureBlank extends ProductFeatureBase {
  type: FeatureType.BLANK;
}

export interface ProductFeatureChecked extends ProductFeatureBase {
  type: FeatureType.CHECKED;
}

export interface ProductFeatureIcon extends ProductFeatureBase {
  type: FeatureType.ICON;
  featureItemId: string;
  icon: UIImage;
  itemLegalCopy?: string;
}

export interface ProductFeatureSubItem extends ProductFeatureBase {
  type: FeatureType.SUB_ITEM;
  featureItemId: string;
  item?: string;
  subItem?: string;
  itemLegalCopy?: string;
}

export interface ModalState {
  backgroundColor?: UIColor;
  footerBackgroundColor?: UIColor;
  textColor?: UIColor;
  selectedTextColor?: UIColor;
  selectedBackgroundColor?: UIColor;
  openButton: UIButton;
  closeButton: UIButton;
  showAdditionalFilterButton: boolean;
  additionalFilterButton?: UIButton;
}
